import type mdast from 'mdast';
import { isBlockLevelContent, isMetadataContent } from '../dom/content';
import { isListElement, isListItemElement, isOrderedListElement, ListElement } from '../dom/list';
import { isElement, isElementWithTagName, isHTMLOrSVGElement, isText, isWhitespaceText } from '../dom/node';
import { isBreakElement, isParagraphElement, isTraditionalParagraphElement } from '../dom/paragraph';
import { nodeToText } from '../htmlToText';
import { isImageElement } from '../image';
import { INSERTION_POINT_FILLER } from '../insertionPoint';
import { someInIterable } from '../iterable';
import { nodeIsLink } from '../link';
import { isBlockquoteElement } from '../quote';
import { styleIsBold } from '../style/bold';
import { styleIsItalic } from '../style/italic';
import { styleHasStrikethrough } from '../style/strikethrough';
import { MarkdownParagraphBuffer } from './markdownParagraphBuffer';
import { MarkdownParentBuffer } from './markdownParentBuffer';
import { MarkdownStyleBuffer } from './markdownStyleBuffer';

/** Produces a Markdown AST from the div/line-based content of a `contenteditable` editor. */
export function markdownAstFromDom(container: Node): mdast.Root {
  return {
    type: 'root',
    children: Array.from(markdownBlockContentFromDomNode(container)),
  };
}

/** Generates block AST nodes from the given DOM container. */
function* markdownBlockContentFromDomNode(container: Node): Iterable<mdast.BlockContent> {
  // Any phrasing content that bubbles up is wrapped in paragraphs.
  const paragraph = new MarkdownParagraphBuffer();

  for (const content of markdownContentFromDomNode(container)) {
    switch (content.type) {
      // Block content.
      case 'paragraph':
      case 'heading':
      case 'thematicBreak':
      case 'blockquote':
      case 'list':
      case 'table':
      case 'code':
        // Flush any buffered phrasing content.
        yield* paragraph.flush();

        // Pass through the block content.
        yield content;
        break;

      // Phrasing content.
      case 'text':
      case 'emphasis':
      case 'strong':
      case 'delete':
      case 'inlineCode':
      case 'break':
      case 'link':
      case 'image':
        // Maybe start a new paragraph.
        if (paragraph.shouldFlushBefore(content)) {
          yield* paragraph.flush();
        }

        // Buffer the phrasing content.
        paragraph.push(content);
        break;

      default:
    }
  }

  // Flush any buffered phrasing content.
  yield* paragraph.flush();
}

// TODO: Reduce duplication with markdownBlockContentFromDomNode().
function* markdownStyledContentFromDomNode(
  parent: mdast.Emphasis | mdast.Strong | mdast.Delete,
  container: Node,
): Iterable<mdast.BlockContent | mdast.PhrasingContent> {
  // Any phrasing content that bubbles up is wrapped in emphasis/strong/strikethrough.
  const style = new MarkdownStyleBuffer(parent);

  for (const content of markdownContentFromDomNode(container)) {
    switch (content.type) {
      // Block content or line breaks.
      case 'paragraph':
      case 'heading':
      case 'thematicBreak':
      case 'blockquote':
      case 'list':
      case 'table':
      case 'code':
      case 'break':
        // Flush any buffered phrasing content.
        yield* style.flush();

        // Pass through the block content or line break.
        yield content;
        break;

      // Phrasing content, except line breaks because we want to hoist them out of the formatting.
      case 'text':
      case 'emphasis':
      case 'strong':
      case 'delete':
      case 'inlineCode':
      case 'link':
        // Buffer the phrasing content.
        style.push(content);
        break;

      default:
    }
  }

  // Flush any buffered phrasing content.
  yield* style.flush();
}

// TODO: Reduce duplication with markdownBlockContentFromDomNode().
function* markdownLinkedContentFromDomNode(
  parent: mdast.Link,
  container: Node,
): Iterable<mdast.BlockContent | mdast.PhrasingContent> {
  // Any static phrasing content that bubbles up is wrapped in a link.
  const link = new MarkdownParentBuffer(parent);

  for (const content of markdownContentFromDomNode(container)) {
    switch (content.type) {
      // Block content.
      case 'paragraph':
      case 'heading':
      case 'thematicBreak':
      case 'blockquote':
      case 'list':
      case 'table':
      case 'code':
      case 'link':
        // Flush any buffered static phrasing content.
        yield* link.flush();

        // Pass through the block content or (illegally) nested link.
        yield content;
        break;

      // Phrasing content.
      case 'text':
      case 'emphasis':
      case 'strong':
      case 'delete':
      case 'inlineCode':
      case 'break':
        // Buffer the static phrasing content.
        link.push(content);
        break;

      default:
    }
  }

  // Flush any buffered phrasing content.
  yield* link.flush();
}

/** Generates a mix of AST nodes from the given DOM nodes. */
function* markdownContentFromDomNode(container: Node): Iterable<mdast.BlockContent | mdast.PhrasingContent> {
  // https://developer.mozilla.org/en-US/docs/Web/API/Document_Object_Model/Whitespace
  const isBlockContext = someInIterable(container.childNodes, isBlockLevelContent);

  // Helps treat inline content as block content when in a block context.
  function* breakIfBlockContext(): Iterable<mdast.Break> {
    if (isBlockContext) {
      yield { type: 'break' };
    }
  }

  for (const domNode of container.childNodes) {
    if (isMetadataContent(domNode)) {
      continue;
    }

    if (isText(domNode) && !(isBlockContext && isWhitespaceText(domNode))) {
      yield {
        type: 'text',
        value: cleanText(domNode.data),
      };
      yield* breakIfBlockContext();
      continue;
    }

    // Emphasis.
    if (
      isHTMLOrSVGElement(domNode) &&
      (['EM', 'I'].includes(domNode.tagName) || styleIsItalic(domNode.style)) &&
      isFlankedStyle(domNode)
    ) {
      yield* markdownStyledContentFromDomNode({ type: 'emphasis', children: [] }, domNode);
      yield* breakIfBlockContext();
      continue;
    }

    // Strong.
    if (
      isHTMLOrSVGElement(domNode) &&
      (['STRONG', 'B'].includes(domNode.tagName) || styleIsBold(domNode.style)) &&
      isFlankedStyle(domNode)
    ) {
      yield* markdownStyledContentFromDomNode({ type: 'strong', children: [] }, domNode);
      yield* breakIfBlockContext();
      continue;
    }

    // Strikethrough.
    if (
      isHTMLOrSVGElement(domNode) &&
      (['S', 'STRIKE', 'DEL'].includes(domNode.tagName) || styleHasStrikethrough(domNode.style)) &&
      isFlankedStyle(domNode)
    ) {
      yield* markdownStyledContentFromDomNode({ type: 'delete', children: [] }, domNode);
      yield* breakIfBlockContext();
      continue;
    }

    // Inline code.
    if (isElementWithTagName(domNode, 'code')) {
      yield {
        type: 'inlineCode',
        value: domNode.textContent || '',
      };
      yield* breakIfBlockContext();
      continue;
    }

    // Line breaks.
    if (isBreakElement(domNode)) {
      yield {
        type: 'break',
      };
      yield* breakIfBlockContext();
      continue;
    }

    // Links.
    if (nodeIsLink(domNode)) {
      yield* markdownLinkedContentFromDomNode(
        {
          type: 'link',
          // Use the raw attribute value. (Instead of the processed href property value.)
          url: domNode.getAttribute('href') || '',
          children: [],
        },
        domNode,
      );
      yield* breakIfBlockContext();
      continue;
    }

    // Code blocks. (<pre> or any block element with pre-formatted white space.)
    if (
      isElementWithTagName(domNode, 'pre') ||
      (isHTMLOrSVGElement(domNode) &&
        isBlockLevelContent(domNode) &&
        (domNode.style.whiteSpace === 'pre' || domNode.style.whiteSpace === 'pre-wrap'))
    ) {
      yield {
        type: 'code',
        // Collapse one trailing newline, if present.
        value: nodeToText(domNode, true).replace(/[\r\n\f]$/, ''),
      };
      continue;
    }

    // Lines. (<div>)
    if (isParagraphElement(domNode)) {
      yield* markdownContentFromDomNode(domNode);
      yield {
        type: 'break',
      };
      continue;
    }

    // Paragraphs. (<p>)
    if (isTraditionalParagraphElement(domNode)) {
      yield {
        type: 'break',
      };
      yield* markdownContentFromDomNode(domNode);
      yield {
        type: 'break',
      };
      yield {
        type: 'break',
      };
      continue;
    }

    // Blockquote.
    if (isBlockquoteElement(domNode)) {
      yield {
        type: 'blockquote',
        children: Array.from(markdownBlockContentFromDomNode(domNode)),
      };
      continue;
    }

    // List.
    if (isListElement(domNode)) {
      yield markdownListFromDomElement(domNode);
      continue;
    }

    // Image (that is not a Front emoji one).
    if (isImageElement(domNode) && !domNode.hasAttribute('data-front-emoji-shortcode')) {
      yield {
        type: 'image',
        url: domNode.getAttribute('src') || '',
      };
      continue;
    }

    // For any other DOM elements, recurse.
    if (isElement(domNode)) {
      if (domNode.hasAttribute('alt')) {
        yield {
          type: 'text',
          value: domNode.getAttribute('alt') || '',
        };
      }

      yield* markdownContentFromDomNode(domNode);
      yield* breakIfBlockContext();
    }
  }
}

function markdownListFromDomElement(element: ListElement): mdast.List {
  const start = element.hasAttribute('start') ? Number(element.getAttribute('start')) : NaN;

  return {
    type: 'list',
    ordered: isOrderedListElement(element),
    start: Number.isFinite(start) ? start : undefined,
    children: Array.from(markdownListContentFromDomNode(element)),
    spread: false,
  };
}

function* markdownListContentFromDomNode(container: Node): Iterable<mdast.ListContent> {
  for (const domNode of container.childNodes) {
    if (isListItemElement(domNode)) {
      yield {
        type: 'listItem',
        spread: false,
        children: Array.from(markdownBlockContentFromDomNode(domNode)),
      };
    } else if (isListElement(domNode)) {
      yield {
        type: 'listItem',
        spread: false,
        children: [markdownListFromDomElement(domNode)],
      };
    }
  }
}

/** Determines if the given style/emphasis container is legally flanked and can be converted to Markdown. */
function isFlankedStyle(node: Node): boolean {
  return isLeftFlankedStyle(node) && isRightFlankedStyle(node);
}

// https://spec.commonmark.org/0.29/#left-flanking-delimiter-run.
const leftFlankedStyleRegExp = /([^\s\p{P}]|(^|[\s\p{P}])\p{P})$/u;

function isLeftFlankedStyle(node: Node): boolean {
  const flank = collectPrecedingFlank(node.previousSibling) + collectFollowingFlank(node.firstChild);
  return leftFlankedStyleRegExp.test(flank);
}

// https://spec.commonmark.org/0.29/#right-flanking-delimiter-run
const rightFlankedStyleRegExp = /^([^\s\p{P}]|\p{P}([\s\p{P}]|$))/u;

function isRightFlankedStyle(node: Node): boolean {
  const flank = collectPrecedingFlank(node.lastChild) + collectFollowingFlank(node.nextSibling);
  return rightFlankedStyleRegExp.test(flank);
}

const precedingFlankRegExp = /\S?\s*$/;

function collectPrecedingFlank(precedingNode: Node | null) {
  let flank = '';
  let node = precedingNode;

  // Iterate backwards through siblings, starting with precedingNode.
  while (node) {
    if (isText(node)) {
      // Prepend all text we encounter.
      flank = `${cleanText(node.data)}${flank}`;
    } else if (isBreakElement(node)) {
      // Treat this as the start of the line.
      break;
    } else if (isElement(node)) {
      // Treat any elements as alphanumeric text.
      flank = `a${flank}`;
      break;
    }

    node = node.previousSibling;
  }

  // Keep only preceding whitespace and one non-whitespace character (if any).
  const match = flank.match(precedingFlankRegExp);
  return (match && match[0]) || '';
}

const followingFlankRegExp = /^\s*\S?/;

function collectFollowingFlank(followingNode: Node | null) {
  let flank = '';
  let node = followingNode;

  // Iterate forwards through siblings, starting with followingNode.
  while (node) {
    // Append all text we encounter.
    if (isText(node)) {
      flank = `${flank}${cleanText(node.data)}`;
    } else if (isBreakElement(node)) {
      // Treat this as the end of the line.
      break;
    } else if (isElement(node)) {
      // Treat any elements as alphanumeric text.
      flank = `${flank}a`;
      break;
    }

    node = node.nextSibling;
  }

  // Keep only following whitespace and one non-whitespace character (if any).
  const match = flank.match(followingFlankRegExp);
  return (match && match[0]) || '';
}

function cleanText(text: string) {
  // TODO: Prevent this stuff from sticking around in the HTML at all.
  return text.replace(INSERTION_POINT_FILLER, '');
}
